/**
 * Runtime Sentry & noisy-extension blocker
 *
 * File-level:
 * - Small runtime module that blocks outgoing Sentry ingestion requests from the browser
 *   and reduces noisy extension-originated console errors that are unrelated to app logic.
 *
 * Usage:
 * - Import this module as early as possible in the app start (e.g. at the top of src/App.tsx).
 *
 * Notes:
 * - This is a development-time convenience. Prefer to fix Sentry initialization (only in prod,
 *   with proper sampling) for production deployments.
 * - The logic is intentionally conservative: it only blocks requests whose target hostname
 *   contains known Sentry ingest domains (e.g. sentry.io / ingest.sentry.io).
 * - It also filters unhandledrejection/error events that match the exact extension message.
 */

const BLOCKED_SENTRY_HOSTS = [
  'ingest.sentry.io',
  'sentry.io',
  '.ingest.us.sentry.io', // includes the host in the error URL provided by you
];

/**
 * isSentryUrl
 *
 * @param urlString string | undefined
 * @returns boolean - true if the URL includes a known sentry ingest host
 * @description Safe check to decide whether to block a network request that targets Sentry.
 */
function isSentryUrl(urlString?: string | null): boolean {
  if (!urlString) return false;
  try {
    const url = new URL(urlString, window.location.href);
    const host = url.host || url.hostname || url.origin || '';
    return BLOCKED_SENTRY_HOSTS.some(h => host.includes(h) || url.href.includes(h));
  } catch {
    // If parsing fails, do a simpler substring test
    return BLOCKED_SENTRY_HOSTS.some(h => (urlString || '').includes(h));
  }
}

/**
 * patchFetch
 *
 * @description Monkey-patch window.fetch to drop requests that target Sentry ingestion endpoints.
 */
function patchFetch() {
  if (typeof window.fetch !== 'function') return;
  const originalFetch = window.fetch.bind(window);
  // @ts-ignore - augmenting window.fetch
  window.fetch = async function (input: RequestInfo, init?: RequestInit) {
    let url: string | undefined;
    if (typeof input === 'string') url = input;
    else if (input && typeof (input as Request).url === 'string') url = (input as Request).url;
    if (isSentryUrl(url)) {
      // Silently discard to avoid spam during development.
      // Return a fake successful Response with 204 No Content semantics.
      return new Response(null, { status: 204, statusText: 'Blocked-Sentry-Dev' });
    }
    return originalFetch(input, init);
  };
}

/**
 * patchSendBeacon
 *
 * @description Patch navigator.sendBeacon to discard any beacon targeting Sentry ingest hosts.
 */
function patchSendBeacon() {
  const nav = navigator as any;
  if (nav && typeof nav.sendBeacon === 'function') {
    const original = nav.sendBeacon.bind(nav);
    nav.sendBeacon = function (url: string, data?: any) {
      if (isSentryUrl(String(url))) {
        // drop silently
        return true; // indicate queued but actually dropped
      }
      return original(url, data);
    };
  }
}

/**
 * patchXHR
 *
 * @description Patch XMLHttpRequest.open to detect Sentry endpoints and abort sending.
 * Useful for SDKs that use XHR under the hood.
 */
function patchXHR() {
  const X = (window as any).XMLHttpRequest;
  if (!X) return;
  const originalOpen = X.prototype.open;
  X.prototype.open = function (method: string, url: string, ...rest: any[]) {
    try {
      if (isSentryUrl(url)) {
        // mark request so send/inner logic can early-exit
        this.__sentryBlocked = true;
      }
    } catch {
      // ignore
    }
    return originalOpen.call(this, method, url, ...rest);
  };
  const originalSend = X.prototype.send;
  X.prototype.send = function (body?: any) {
    if (this.__sentryBlocked) {
      // pretend it was sent successfully
      this.readyState = 4;
      this.status = 204;
      // Fire onreadystatechange if present
      try {
        if (typeof this.onreadystatechange === 'function') {
          this.onreadystatechange();
        }
        if (typeof this.onload === 'function') {
          this.onload();
        }
      } catch {
        // ignore handler errors
      }
      return;
    }
    return originalSend.call(this, body);
  };
}

/**
 * suppressExtensionErrors
 *
 * @description Adds handlers to reduce noisy console errors typically raised by browser extensions.
 * - Suppresses unhandledrejection messages that exactly match the extension's message seen in the console:
 *   "A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received"
 *
 * This is intentionally narrowly targeted to avoid hiding app errors.
 */
function suppressExtensionErrors() {
  const EXT_MSG = 'A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received';

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    try {
      const reason = ev?.reason;
      if (typeof reason === 'string' && reason.includes(EXT_MSG)) {
        // prevent the default logging for this very specific extension message
        ev.preventDefault();
        // Small, non-intrusive log so developer knows it was suppressed
        // eslint-disable-next-line no-console
        console.warn('[sentry-blocker] suppressed browser-extension messaging error');
      }
      // If reason is an Error object with message
      if (reason && typeof (reason as any).message === 'string' && (reason as any).message.includes(EXT_MSG)) {
        ev.preventDefault();
        // eslint-disable-next-line no-console
        console.warn('[sentry-blocker] suppressed browser-extension messaging error (object reason)');
      }
    } catch (err) {
      // ignore
    }
  });

  // Also catch global error events that include the message
  window.addEventListener('error', (ev: ErrorEvent) => {
    try {
      if (ev?.message && typeof ev.message === 'string' && ev.message.includes(EXT_MSG)) {
        ev.preventDefault();
        // eslint-disable-next-line no-console
        console.warn('[sentry-blocker] suppressed browser-extension messaging error (error event)');
      }
    } catch {
      // ignore
    }
  });
}

/**
 * initBlocker
 *
 * @description Initializes all patches. Safe to call multiple times.
 */
export function initBlocker() {
  try {
    patchFetch();
    patchSendBeacon();
    patchXHR();
    suppressExtensionErrors();
    // eslint-disable-next-line no-console
    console.info('[sentry-blocker] initialized (Sentry requests will be blocked in this session)');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sentry-blocker] failed to initialize', err);
  }
}

// Initialize immediately when module is imported
initBlocker();